import { useEffect, useState } from 'react';
import type { VavClient } from '@vav/client-core';
import { useT } from '../i18n.tsx';

interface Props {
  client: VavClient;
  onClose: () => void;
}

/**
 * Invite friends.
 *
 * The link both points at the app and says who is inviting, so the person who
 * opens it lands on signup already knowing whose invitation it is — and once
 * they register, the conversation with the inviter is already open. An empty
 * app with no contacts is the fastest way to lose someone in the first minute.
 */
/** Desktop browsers often lack the share sheet, whatever the DOM types claim. */
const canShare = typeof navigator !== 'undefined' && 'share' in navigator;

export function InviteDialog({ client, onClose }: Props) {
  const t = useT();
  const [link, setLink] = useState<string>();
  const [busy, setBusy] = useState(true);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    void (async () => {
      try {
        // The link must be reachable by someone who does not have the app, so
        // it is built from wherever this page is actually served.
        const invite = await client.createInviteLink(window.location.origin);
        setLink(invite.url);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setBusy(false);
      }
    })();
  }, [client]);

  const message = link
    ? `${t('invite.message', { name: client.account?.displayName ?? t('invite.defaultName') })}
${link}`
    : '';

  async function share() {
    if (!link) return;
    // The native share sheet is the whole point on a phone: it puts the link
    // straight into WhatsApp, SMS or wherever the friend actually is.
    if (canShare) {
      try {
        await navigator.share({ title: 'VAV', text: message });
        return;
      } catch {
        // Cancelled, or unsupported for this payload — fall through to copy.
      }
    }
    await copy();
  }

  async function copy() {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError(t('invite.copyFailed'));
    }
  }

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="card dialog" role="dialog" aria-modal="true" aria-label={t('invite.title')}>
        <header className="dialog__header">
          <h2>{t('invite.title')}</h2>
          <button className="icon" onClick={onClose} aria-label={t('invite.close')}>
            ✕
          </button>
        </header>

        <p className="muted">
          {t('invite.body')}
        </p>

        {busy && <p className="muted">{t('invite.preparing')}</p>}
        {error && <p className="error">{error}</p>}

        {link && (
          <>
            <input className="inviteLink" value={link} readOnly dir="ltr" onFocus={(e) => e.target.select()} />

            <button className="primary" onClick={() => void share()}>
              {canShare ? `📤 ${t('invite.share')}` : copied ? `✓ ${t('invite.copied')}` : `📋 ${t('invite.copy')}`}
            </button>
            {canShare && (
              <button className="ghost" onClick={() => void copy()}>
                {copied ? `✓ ${t('invite.copied')}` : `📋 ${t('invite.copy')}`}
              </button>
            )}

            <p className="hint">
              {t('invite.caution')}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
